import type { FC } from "react";
import { ASSISTANT_AVATAR } from "@/theme/tokens";
import { AssistantAvatarMood } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { AVATAR_PART } from "@/ui/AssistantAvatar/assistantAvatarMotion";

const { ACCESSORY } = ASSISTANT_AVATAR;

/** A cut-out on the face, with the point its candlelit inside shrinks toward. */
interface CarvedHole {
	path: string;
	centerX: number;
	centerY: number;
}

// The glow is the hole shrunk and dropped a little, so the dark cut wall shows thickest along the top edge.
const GLOW_SCALE = 0.74;
const GLOW_DROP = 1.6;

// Triangle eyes whose inner corners dip, for a mischievous scowl over the grin.
const EYES: CarvedHole[] = [
	{ path: "M35 63 L57 67 L49 48 Z", centerX: 47, centerY: 60 },
	{ path: "M85 63 L63 67 L71 48 Z", centerX: 73, centerY: 60 },
];

const GRIN: CarvedHole = {
	path: "M33 72 Q42 80 51 80 L53 86 L58 86 L59 81 Q72 81 87 72 Q80 92 68 94 L67 88 L61 88 L60 95 Q41 95 33 72 Z",
	centerX: 60,
	centerY: 85,
};

const MOUTHS: Record<AssistantAvatarMood, CarvedHole> = {
	[AssistantAvatarMood.Idle]: GRIN,
	[AssistantAvatarMood.NeedsYou]: GRIN,
	[AssistantAvatarMood.Done]: GRIN,
	[AssistantAvatarMood.Thinking]: { path: "M50 82 L72 78 L72 84 L50 88 Z", centerX: 61, centerY: 83 },
	[AssistantAvatarMood.Talking]: { path: "M51 83 C51 74 69 74 69 83 C69 92 51 92 51 83 Z", centerX: 60, centerY: 83 },
	[AssistantAvatarMood.Oops]: {
		path: "M42 83 L49 77 L55 83 L61 77 L67 83 L73 77 L78 81 L78 86 L73 82 L67 88 L61 82 L55 88 L49 82 L42 88 Z",
		centerX: 60,
		centerY: 83,
	},
};

const CarvedCutout: FC<{ hole: CarvedHole; candleFill: string }> = ({ hole, candleFill }) => (
	<>
		<path d={hole.path} fill={ACCESSORY.CARVED_WALL} />
		<path
			className={AVATAR_PART.CANDLE}
			d={hole.path}
			fill={candleFill}
			transform={`translate(${hole.centerX} ${hole.centerY + GLOW_DROP}) scale(${GLOW_SCALE}) translate(${-hole.centerX} ${-hole.centerY})`}
		/>
	</>
);

/**
 * The pumpkin costume's carved face, lit by a candle inside: scowling triangle eyes and a toothy grin that
 * changes shape with the mood. Replaces the usual eyes and mouth.
 */
export const JackOLanternFace: FC<{ mood: AssistantAvatarMood; idPrefix: string }> = ({ mood, idPrefix }) => {
	const candleId = `${idPrefix}-candle`;
	const spillId = `${idPrefix}-candle-spill`;
	const candleFill = `url(#${candleId})`;
	return (
		<>
			<defs>
				<radialGradient id={candleId} gradientUnits="userSpaceOnUse" cx={60} cy={74} r={30}>
					<stop offset={0} stopColor={ACCESSORY.CANDLE_CORE} />
					<stop offset={1} stopColor={ACCESSORY.CANDLE} />
				</radialGradient>
				<radialGradient id={spillId}>
					<stop offset={0} stopColor={ACCESSORY.CANDLE_SPILL} />
					<stop offset={1} stopColor={ACCESSORY.CANDLE_SPILL} stopOpacity={0} />
				</radialGradient>
			</defs>
			<ellipse className={AVATAR_PART.CANDLE} cx={60} cy={74} rx={40} ry={30} fill={`url(#${spillId})`} />
			<g className={AVATAR_PART.EYES}>
				{EYES.map((eye) => (
					<CarvedCutout key={eye.path} hole={eye} candleFill={candleFill} />
				))}
			</g>
			<g className={AVATAR_PART.MOUTH}>
				<CarvedCutout hole={MOUTHS[mood]} candleFill={candleFill} />
			</g>
		</>
	);
};
